export interface AttachmentLike {
    file_name: string;
    version?: string | number | null;
    file_path?: string;
    url?: string;
}

export interface DisplayAttachment<T> {
    file: T;
    displayName: string;
    rowKey: string;
}

const hasVersion = (version?: string | number | null) =>
    version !== undefined &&
    version !== null &&
    String(version).trim() !== '';

// Newest version first; files without version go to the bottom of the group
const compareVersionDesc = (a: AttachmentLike, b: AttachmentLike) => {
    const aHas = hasVersion(a.version);
    const bHas = hasVersion(b.version);
    if (!aHas && !bHas) return 0;
    if (!aHas) return 1;
    if (!bHas) return -1;
    return String(b.version).localeCompare(String(a.version), undefined, {
        numeric: true
    });
};

/**
 * Groups attachments with the exact same file_name, sorts each duplicated group
 * by version (newest first) and appends "[version x]" to the duplicated ones.
 * Groups keep the position of their first file in the original list.
 */
export const buildAttachmentDisplayList = <T extends AttachmentLike>(
    files: T[]
): DisplayAttachment<T>[] => {
    const groups = new Map<string, T[]>();
    files.forEach(file => {
        const group = groups.get(file.file_name);
        if (group) {
            group.push(file);
        } else {
            groups.set(file.file_name, [file]);
        }
    });

    const result: DisplayAttachment<T>[] = [];
    groups.forEach(group => {
        const isDuplicated = group.length > 1;
        // Array.prototype.sort is not guaranteed stable on older engines
        const ordered = isDuplicated
            ? group
                  .map((file, index) => ({ file, index }))
                  .sort(
                      (a, b) =>
                          compareVersionDesc(a.file, b.file) || a.index - b.index
                  )
                  .map(item => item.file)
            : group;

        ordered.forEach(file => {
            result.push({
                file,
                displayName:
                    isDuplicated && hasVersion(file.version)
                        ? `${file.file_name} [version ${file.version}]`
                        : file.file_name,
                rowKey: `${result.length}_${file.file_path ?? file.url ?? file.file_name}`
            });
        });
    });

    return result;
};
